import { isClarityName, parsePrincipal } from "../utils/address.ts";
import { c32address, c32addressDecode } from "../utils/c32.ts";
import {
	type IntegerType,
	asciiToBytes,
	bytesToHex,
	bytesToTwosBigInt,
	hexToBytes,
	intToBigInt,
	utf8ToBytes,
} from "../utils/encoding.ts";
import { deserializeCVBytes } from "./deserialize.ts";
import { serializeCVBytes } from "./serialize.ts";
import type {
	BooleanCV,
	BufferCV,
	ClarityValue,
	ContractPrincipalCV,
	FalseCV,
	IntCV,
	ListCV,
	NoneCV,
	ResponseErrorCV,
	ResponseOkCV,
	SomeCV,
	StandardPrincipalCV,
	StringAsciiCV,
	StringUtf8CV,
	TrueCV,
	TupleCV,
	TupleData,
	UIntCV,
} from "./types.ts";

const MAX_U128 = BigInt("0xffffffffffffffffffffffffffffffff");
const MIN_I128 = BigInt("-170141183460469231731687303715884105728");
const MAX_I128 = BigInt("0x7fffffffffffffffffffffffffffffff");

// Primitives

export function intCV(value: IntegerType): IntCV {
	let v: IntegerType = value;
	if (typeof v === "string" && v.toLowerCase().startsWith("0x")) {
		v = bytesToTwosBigInt(hexToBytes(v));
	}
	if (v instanceof Uint8Array) v = bytesToTwosBigInt(v);
	const n = intToBigInt(v);
	if (n > MAX_I128) throw new RangeError(`Int exceeds max i128: ${MAX_I128}`);
	if (n < MIN_I128) throw new RangeError(`Int below min i128: ${MIN_I128}`);
	return { type: "int", value: n };
}

export function uintCV(value: IntegerType): UIntCV {
	const n = intToBigInt(value);
	if (n < 0n)
		throw new RangeError("Cannot construct unsigned int from negative value");
	if (n > MAX_U128) throw new RangeError(`UInt exceeds max u128: ${MAX_U128}`);
	return { type: "uint", value: n };
}

export const trueCV = (): TrueCV => ({ type: "true" });
export const falseCV = (): FalseCV => ({ type: "false" });
export const boolCV = (v: boolean): BooleanCV => (v ? trueCV() : falseCV());

export function bufferCV(buffer: Uint8Array): BufferCV {
	if (buffer.byteLength > 1_048_576) {
		throw new Error("Buffer exceeds max size of 1MB");
	}
	return { type: "buffer", value: bytesToHex(buffer) };
}

export function standardPrincipalCV(address: string): StandardPrincipalCV {
	// Validate by decoding
	const [version, hash160] = c32addressDecode(address);
	const normalized = c32address(version, hash160);
	return { type: "address", value: normalized };
}

export function contractPrincipalCV(
	address: string,
	contractName: string,
): ContractPrincipalCV {
	const [version, hash160] = c32addressDecode(address);
	const normalized = c32address(version, hash160);
	if (utf8ToBytes(contractName).byteLength >= 128) {
		throw new Error("Contract name must be less than 128 bytes");
	}
	return { type: "contract", value: `${normalized}.${contractName}` };
}

export function noneCV(): NoneCV {
	return { type: "none" };
}

export function someCV(value: ClarityValue): SomeCV {
	return { type: "some", value };
}

export function responseOkCV(value: ClarityValue): ResponseOkCV {
	return { type: "ok", value };
}

export function responseErrorCV(value: ClarityValue): ResponseErrorCV {
	return { type: "err", value };
}

export function listCV(values: ClarityValue[]): ListCV {
	return { type: "list", value: values };
}

export function tupleCV(data: TupleData): TupleCV {
	for (const key in data) {
		if (!isClarityName(key)) {
			throw new Error(`"${key}" is not a valid Clarity name`);
		}
	}
	return { type: "tuple", value: data };
}

export function stringAsciiCV(value: string): StringAsciiCV {
	return { type: "ascii", value };
}

export function stringUtf8CV(value: string): StringUtf8CV {
	return { type: "utf8", value };
}

// Cl namespace — clean API

export const Cl: {
	readonly int: (value: IntegerType) => IntCV;
	readonly uint: (value: IntegerType) => UIntCV;
	readonly bool: (v: boolean) => BooleanCV;
	principal(address: string): StandardPrincipalCV | ContractPrincipalCV;
	address(address: string): StandardPrincipalCV | ContractPrincipalCV;
	readonly contractPrincipal: (
		address: string,
		contractName: string,
	) => ContractPrincipalCV;
	readonly standardPrincipal: (address: string) => StandardPrincipalCV;
	readonly buffer: (buffer: Uint8Array) => BufferCV;
	readonly bufferFromHex: (hex: string) => BufferCV;
	readonly bufferFromAscii: (ascii: string) => BufferCV;
	readonly bufferFromUtf8: (utf8: string) => BufferCV;
	readonly none: () => NoneCV;
	readonly some: (value: ClarityValue) => SomeCV;
	readonly ok: (value: ClarityValue) => ResponseOkCV;
	readonly error: (value: ClarityValue) => ResponseErrorCV;
	readonly list: (values: ClarityValue[]) => ListCV;
	readonly tuple: (data: TupleData) => TupleCV;
	readonly stringAscii: (value: string) => StringAsciiCV;
	readonly stringUtf8: (value: string) => StringUtf8CV;
	serialize(value: ClarityValue): string;
	readonly deserialize: (bytes: Uint8Array) => ClarityValue;
} = {
	int: intCV,
	uint: uintCV,
	bool: boolCV,
	principal(address: string): StandardPrincipalCV | ContractPrincipalCV {
		const parsed = parsePrincipal(address);
		if (!parsed) throw new Error(`Invalid principal: ${address}`);
		return parsed.contractName
			? contractPrincipalCV(parsed.address, parsed.contractName)
			: standardPrincipalCV(parsed.address);
	},
	address(address: string): StandardPrincipalCV | ContractPrincipalCV {
		return Cl.principal(address);
	},
	contractPrincipal: contractPrincipalCV,
	standardPrincipal: standardPrincipalCV,
	buffer: bufferCV,
	bufferFromHex: (hex: string): BufferCV => bufferCV(hexToBytes(hex)),
	bufferFromAscii: (ascii: string): BufferCV => bufferCV(asciiToBytes(ascii)),
	bufferFromUtf8: (utf8: string): BufferCV => bufferCV(utf8ToBytes(utf8)),
	none: noneCV,
	some: someCV,
	ok: responseOkCV,
	error: responseErrorCV,
	list: listCV,
	tuple: tupleCV,
	stringAscii: stringAsciiCV,
	stringUtf8: stringUtf8CV,
	serialize(value: ClarityValue): string {
		return bytesToHex(serializeCVBytes(value));
	},
	deserialize: deserializeCVBytes,
} as const;
